// @ts-nocheck
import { getOrderState, OrderStateContext } from "@insite/client-framework/Store/Data/Orders/OrdersSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Typography, { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import Block, { BlockProps } from "@store/Library/Components/Toolkit/Block/Block";
import { infoProps } from "@store/Themes/common/props/info";
import { orderProps } from "@store/Themes/common/props/order";
import getWidgetComponent from "@toolkit/Framework/Common/Utilities/getWidgetComponent";
import { useSettingsCollection } from "@toolkit/Framework/Store/Context/ContextHooks";
import React, { FC, useContext } from "react";

const Date = getWidgetComponent("OrderDetails/OrderDate");
const Notes = getWidgetComponent("OrderDetails/OrderDetailsNotes");
const Status = getWidgetComponent("OrderDetails/OrderDetailsStatus");
const Salesperson = getWidgetComponent("OrderDetails/OrderSalesperson");
const RequestedDate = getWidgetComponent("OrderDetails/OrderDetailsRequestedDate");
const PaymentTerms = getWidgetComponent("OrderDetails/OrderDetailsPaymentTerms");
const PoNumber = getWidgetComponent("OrderDetails/OrderPo");
const ShippingAddress = getWidgetComponent("OrderDetails/OrderDetailsShippingAddress");
const BillingAddress = getWidgetComponent("OrderDetails/OrderDetailsBillingAddress");
const ShippingMethod = getWidgetComponent("OrderDetails/OrderDetailsShippingMethod");
const ShipmentPackages = getWidgetComponent("OrderDetails/OrderDetailsShipmentPackages");
const VatNumber = getWidgetComponent("OrderDetails/OrderDetailsVatNumber");
const VmiLocation = getWidgetComponent("OrderDetails/OrderDetailsVmiLocation");

interface Props extends WidgetProps {
    orderState?: ReturnType<typeof getOrderState>;
}

export interface OrderDetailsInformationStyles {
    block?: BlockProps;
    orderNumberHeading?: TypographyProps;
    infoGridContainer?: GridContainerProps;
    infoGridItem?: GridItemProps;
    infoLabel?: TypographyPresentationProps;
    infoValue?: TypographyPresentationProps;
    notesGridItem?: GridItemProps;
    shippingInformationTitle?: TypographyProps;
    billingInformationTitle?: TypographyProps;
}

export const orderDetailsInformationStyles: OrderDetailsInformationStyles = {
    block: orderProps.block,
    orderNumberHeading: orderProps.subtitle,
    infoGridContainer: infoProps.grid?.container,
    infoGridItem: infoProps.grid?.item,
    notesGridItem: infoProps.grid?.largeItem,
    shippingInformationTitle: orderProps.subtitle,
    billingInformationTitle: orderProps.subtitle,
};

const styles = orderDetailsInformationStyles;

const OrderDetailsInformation: FC<Props> = ({ orderState: baseOrderState }) => {
    const { orderSettings, productSettings } = useSettingsCollection();
    const orderStateContext = useContext(OrderStateContext);
    const orderState = baseOrderState || orderStateContext;
    const order = orderState.value;

    if (!order) {
        return null;
    }

    const showPoNumber = orderSettings.showPoNumber && order.customerPO;
    const showVatNumber = productSettings.enableVat && order.customerVatNumber;
    const showTerms = orderSettings.showTermsCode && order.terms;
    const showInformations =
        order.orderDate ||
        showPoNumber ||
        order.statusDisplay ||
        order.salesperson ||
        showVatNumber ||
        showTerms ||
        order.vmiLocationName;

    return (
        <OrderStateContext.Provider value={orderState}>
            <Block {...styles.block}>
                <Typography {...styles.orderNumberHeading}>
                    {translate("Order #")}
                    {order.erpOrderNumber ? order.erpOrderNumber : order.webOrderNumber}
                </Typography>
                {showInformations ? (
                    <GridContainer {...styles.infoGridContainer}>
                        {order.orderDate ? (
                            <GridItem {...styles.infoGridItem}>
                                <Date />
                            </GridItem>
                        ) : null}
                        {showPoNumber ? (
                            <GridItem {...styles.infoGridItem}>
                                <PoNumber />
                            </GridItem>
                        ) : null}
                        {order.statusDisplay ? (
                            <GridItem {...styles.infoGridItem}>
                                <Status />
                            </GridItem>
                        ) : null}
                        {order.salesperson ? (
                            <GridItem {...styles.infoGridItem}>
                                <Salesperson />
                            </GridItem>
                        ) : null}
                        {showVatNumber ? (
                            <GridItem {...styles.infoGridItem}>
                                <VatNumber />
                            </GridItem>
                        ) : null}
                        {showTerms ? (
                            <GridItem {...styles.infoGridItem}>
                                <PaymentTerms />
                            </GridItem>
                        ) : null}
                        {order.vmiLocationName ? (
                            <GridItem {...styles.infoGridItem}>
                                <VmiLocation />
                            </GridItem>
                        ) : null}
                    </GridContainer>
                ) : null}
                {order.notes ? (
                    <GridContainer {...styles.infoGridContainer}>
                        <GridItem {...styles.notesGridItem}>
                            <Notes />
                        </GridItem>
                    </GridContainer>
                ) : null}
            </Block>
            <Block {...styles.block}>
                <Typography {...styles.billingInformationTitle}>{translate("Billing Information")}</Typography>
                <BillingAddress />
            </Block>
            <Block {...styles.block}>
                <Typography {...styles.shippingInformationTitle}>{translate("Shipping Information")}</Typography>
                <GridContainer {...styles.infoGridContainer}>
                    <GridItem {...styles.infoGridItem}>
                        <ShippingAddress />
                    </GridItem>
                    {order.requestedDeliveryDateDisplay ? (
                        <GridItem {...styles.infoGridItem}>
                            <RequestedDate />
                        </GridItem>
                    ) : null}
                    {order.shipViaDescription || order.shipCode ? (
                        <GridItem {...styles.infoGridItem}>
                            <ShippingMethod />
                        </GridItem>
                    ) : null}
                </GridContainer>
                {order.shipmentPackages && order.shipmentPackages.length > 0 && <ShipmentPackages />}
            </Block>
        </OrderStateContext.Provider>
    );
};

const widgetModule: WidgetModule = {
    component: OrderDetailsInformation,
    definition: {
        group: "Order Details",
        displayName: "Information",
        allowedContexts: ["OrderDetailsPage", "VmiOrderDetailsPage"],
    },
};

export default widgetModule;
